import CompletedIcon from "../../../assets/svg/completed-icon";
import HomeIcon from "../../../assets/svg/home-icon";
import { getRegionLabel } from "../../../utils/constants/mappings";

/** Scrape job status values received from backend */
export const ScrapeJobStatus = {
  INITIATED: "initiated",
  SCRAPING_LINKS: "scraping_links",
  COMPLETED_LINK_SCRAPE: "completed_link_scrape",
  FAILED_LINK_SCRAPE: "failed_link_scrape",
  SCRAPING_CONTENT: "scraping_content",
  COMPLETED_CONTENT_SCRAPE: "completed_content_scrape",
  PARTIALLY_COMPLETED_CONTENT_SCRAPE: "partially_completed_content_scrape",
  FAILED_CONTENT_SCRAPE: "failed_content_scrape",
} as const;

export type ScrapeJobStatusType =
  (typeof ScrapeJobStatus)[keyof typeof ScrapeJobStatus];

/** Scrape node status values received from backend */
export const ScrapeNodeStatus = {
  PENDING: "pending_scrape",
  INITIATED: "initiated_scrape",
  SCRAPING: "scraping_contents",
  COMPLETED: "completed_scrape",
  FAILED: "failed_scrape",
} as const;

export type ScrapeNodeStatusType =
  (typeof ScrapeNodeStatus)[keyof typeof ScrapeNodeStatus];

export const getTransformPropertiesOptions = (properties: {
  res: { name: string; id: string }[];
}) => [
  {
    value: "-1",
    label: "Generic",
    content: (
      <span className="text-md font-medium text-heading-text dark:text-heading-text-dk flex items-center gap-2">
        <CompletedIcon className="w-4 h-4 text-verba-blue  dark:text-verba-blue-dk" />
        Generic
      </span>
    ),
    selectedOptionContent: (
      <span className="text-md font-medium text-verba-blue dark:text-verba-blue-dk flex items-center gap-2">
        <CompletedIcon className="w-4 h-4 text-verba-blue  dark:text-verba-blue-dk" />
        Generic
      </span>
    ),
  },
  ...(properties?.res?.map((property: { name: string; id: string }) => ({
    value: property?.id,
    label: property?.name,
    content: (
      <span className="text-md font-medium text-heading-text dark:text-heading-text-dk flex items-center gap-2">
        <HomeIcon className="w-4 h-4 text-gray-placeholder dark:text-gray-placeholder-dk" />
        {property?.name}
      </span>
    ),
    selectedOptionContent: (
      <span className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk flex items-center gap-2">
        <HomeIcon className="w-4 h-4 text-gray-placeholder dark:text-gray-placeholder-dk" />
        {property?.name}
      </span>
    ),
  })) || []),
];

export const PLAYGROUND_CONFIG = {
  PAGINATION: {
    ITEMS_PER_PAGE: 10,
  },
  FILE_UPLOAD: {
    MAX_FILES: 5,
    MAX_FILE_SIZE: 45,
    ACCEPTED_FILE_TYPES: ".docx, .pdf, .xlsx",
  },
  EMPTY_STATES: {
    DOCUMENTS: {
      NO_SEARCH_RESULTS:
        "No documents found. Try adjusting your search or upload new documents.",
      NO_DOCUMENTS: "Upload Document to Knowledge Base",
      ERROR: "Error fetching documents",
    },
    WEBSITES: {
      NO_SEARCH_RESULTS: "No websites found matching your search",
      NO_WEBSITES: "Auto-Sync from Website",
      ERROR: "Error fetching websites",
    },
  },
  TABS: {
    DEFAULT_TAB: "knowledge-base" as const,
    VALID_TABS: [
      "content",
      "testing",
      "debug",
      "knowledge-base",
      "personality",
      "logs",
    ] as const,
    LIST: [
      {
        id: "knowledge-base",
        label: "Knowledge Base",
      },
      {
        id: "testing",
        label: "Testing",
      },
      {
        id: "debug",
        label: "Debug",
      },
      // {
      //   id: "personality",
      //   label: "Personality",
      // },
      {
        id: "logs",
        label: "Logs",
      },
    ] as const,
  },
  AUTO_SYNC_LIST: {
    HEADERS: [
      {
        id: "website",
        label: "Website",
        width: "w-1/3",
      },
      {
        id: "liveStatus",
        label: "Live Status",
        width: "w-1/4",
      },
      {
        id: "playgroundStatus",
        label: "Playground Status",
        width: "w-1/4",
      },
      {
        id: "lastSync",
        label: "Last sync on",
        width: "w-1/4",
      },
    ] as const,
    STATUS_STYLES: {
      LIVE: {
        containerClass:
          "bg-user-management-green-card-bg dark:bg-user-management-green-card-bg-dk",
        textClass: "text-verba-green-success dark:text-verba-green-success-dk",
        text: "Live",
      },
      PUBLISHED: {
        containerClass:
          "bg-user-management-green-card-bg dark:bg-user-management-green-card-bg-dk",
        textClass: "text-verba-green-success dark:text-verba-green-success-dk",
        text: "Live",
      },
      PROCESSING: {
        containerClass: "bg-card-yellow-bg dark:bg-card-yellow-bg-dk",
        textClass: "text-verba-indigo dark:text-verba-indigo-dk",
        text: "Processing",
      },
      INITIATED: {
        containerClass: "bg-card-yellow-bg dark:bg-card-yellow-bg-dk",
        textClass: "text-verba-indigo dark:text-verba-indigo-dk",
        text: "Processing",
      },
      RETRYING: {
        containerClass: "bg-card-yellow-bg dark:bg-card-yellow-bg-dk",
        textClass: "text-verba-indigo dark:text-verba-indigo-dk",
        text: "Processing",
      },
      COMPLETED: {
        containerClass: "bg-green-100",
        textClass: "text-green-500",
        text: "Completed",
      },
      FAILED: {
        containerClass:
          "bg-user-management-red-card-bg dark:bg-user-management-red-card-bg-dk",
        textClass: "text-verba-red-alert dark:text-verba-red-alert-dk",
        text: "Failed",
      },
      INGESTION_FAILED: {
        containerClass: "bg-red-50",
        textClass: "text-red-500",
        text: "Failed",
      },
      DRAFT: {
        containerClass: "bg-border-gray dark:bg-border-gray-dk",
        textClass: "text-dark-gray dark:text-dark-gray-dk",
        text: "Draft",
      },
      DELETED: {
        containerClass:
          "bg-user-management-red-card-bg dark:bg-user-management-red-card-bg-dk",
        textClass: "text-verba-red-alert dark:text-verba-red-alert-dk",
        text: "Deleted",
      },
      DELETING: {
        containerClass:
          "bg-user-management-red-card-bg dark:bg-user-management-red-card-bg-dk",
        textClass: "text-verba-red-alert dark:text-verba-red-alert-dk",
        text: "Deleting",
      },
      DELINKED: {
        containerClass: "bg-purple-50",
        textClass: "text-purple-500",
        text: "Delinked",
      },
      DEFAULT: {
        containerClass: "bg-gray-50",
        textClass: "text-gray-500",
        text: "-",
      },
    } as const,
    WEB_SYNC_STATUS_STYLES: {
      INITIATED: {
        containerClass: "bg-applied-filters-tag-bg dark:bg-applied-filters-tag-bg-dk",
        textClass: "text-verba-blue dark:text-verba-blue-dk",
        text: "Syncing",
      },
      SCRAPING_LINKS: {
        containerClass: "bg-applied-filters-tag-bg dark:bg-applied-filters-tag-bg-dk",
        textClass: "text-verba-blue dark:text-verba-blue-dk",
        text: "Extracting links",
      },
      COMPLETED_LINK_SCRAPE: {
        containerClass:
          "bg-applied-filters-tag-bg dark:bg-applied-filters-tag-bg-dk",
        textClass: "text-verba-indigo dark:text-verba-indigo-dk",
        text: "All Links extracted",
      },
      FAILED_LINK_SCRAPE: {
        containerClass: "bg-applied-filters-tag-bg dark:bg-applied-filters-tag-bg-dk",
        textClass: "text-red-500",
        text: "Extraction error",
      },
      SCRAPING_CONTENT: {
        containerClass: "bg-applied-filters-tag-bg dark:bg-applied-filters-tag-bg-dk",
        textClass: "text-verba-blue dark:text-verba-blue-dk",
        text: "Extracting content",
      },
      COMPLETED_CONTENT_SCRAPE: {
        containerClass:
          "bg-applied-filters-tag-bg dark:bg-applied-filters-tag-bg-dk",
        textClass: "text-verba-indigo dark:text-verba-indigo-dk",
        text: "All Content Extracted",
      },
      FAILED_CONTENT_SCRAPE: {
        containerClass: "bg-applied-filters-tag-bg dark:bg-applied-filters-tag-bg-dk",
        textClass: "text-verba-red-alert dark:text-verba-red-alert-dk",
        text: "Content extraction error",
      },
      PARTIALLY_COMPLETED_CONTENT_SCRAPE: {
        containerClass:
          "bg-applied-filters-tag-bg dark:bg-applied-filters-tag-bg-dk",
        textClass: "text-rating-yellow dark:text-rating-yellow-dk",
        text: "Partial Content Extracted",
      },
    } as const,
    SCRAPE_NODE_STATUS_STYLES: {
      PENDING_SCRAPE: {
        containerClass:
          "bg-card-yellow-bg dark:bg-card-yellow-bg-dk",
        textClass: "text-heading-text dark:text-heading-text-dk",
        text: "Pending",
      },
      INITIATED_SCRAPE: {
        containerClass: "bg-transparent",
        textClass: "text-verba-blue dark:text-verba-blue-dk",
        text: "Syncing",
      },
      SCRAPING_CONTENTS: {
        containerClass: "bg-transparent",
        textClass: "text-verba-indigo dark:text-verba-indigo-dk",
        text: "Extracting Content",
      },
      COMPLETED_SCRAPE: {
        containerClass:
          "bg-applied-filters-tag-bg dark:bg-applied-filters-tag-bg-dk",
        textClass: "text-verba-indigo dark:text-verba-indigo-dk",
        text: "Completed",
      },
      FAILED_SCRAPE: {
        containerClass: "bg-transparent",
        textClass: "text-verba-red-alert dark:text-verba-red-alert-dk",
        text: "Failed",
      },
    } as const,
    MENU_ITEMS: {
      GO_LIVE: {
        operation: "publish",
        icon: "NavigateIcon",
        text: "Go Live",
        action: "go_live" as const,
        className: "text-gray-700",
        iconClassName: "text-gray-700",
        textClassName: "text-gray-700",
      },
      DELETE_FROM_PLAYGROUND: {
        operation: "draft_delete",
        icon: "DeleteIcon",
        text: "Delete from Playground",
        action: "delete_from_playground" as const,
        className: "text-red-600",
        iconClassName: "text-red-600",
        textClassName: "text-red-600 whitespace-nowrap",
      },
      DELETE_FROM_LIVE: {
        operation: "publish_delete",
        icon: "DeleteIcon",
        text: "Delete from Live",
        action: "delete_from_live" as const,
        className: "text-red-600",
        iconClassName: "text-red-600",
        textClassName: "text-red-600",
      },
      RESYNC: {
        operation: "reingest",
        icon: "RefreshIcon",
        text: "Resync",
        action: "resync" as const,
        className: "text-gray-700",
        iconClassName: "text-gray-700",
        textClassName: "text-gray-700",
      },
    } as const,
  },
  DIRECTORY_SECTION: {
    TITLE: "Sync content from website",
    ADD_WEBSITE: "Add Website",
    WEBSITE_LINKS: "Website Links",
    SEARCH_PLACEHOLDER: "Search URL",
    NO_WEBSITES: "No website urls found",
    SYNC_CONTENT: "Sync content",
    HEADERS: [
      { id: "websiteName", label: "Website Name", width: "w-1/4" },
      { id: "jobId", label: "Job", width: "w-1/4" },
      { id: "pages", label: "No of pages", width: "w-1/5" },
      { id: "status", label: "Status", width: "w-1/4" },
      { id: "lastSync", label: "Last sync on", width: "w-1/4" },
    ],
    STATUS_MODAL: {
      TITLE: "Status",
    },
    MESSAGES: {
      PUBLISH_SUCCESS: "Publish in progress",
      PUBLISH_ERROR: "Publish failed",
    },
  },
  DOCUMENT_STATUS: {
    LIVE: "Live",
    DRAFT: "Draft",
    PROCESSING: "Processing",
    RETRYING: "Retrying",
    FAILED: "Failed",
    DELETED: "Deleted",
    DELETING: "Deleting",
    DELINKED: "Delinked",
  },
  WEB_SYNC_STATUS: {
    TITLE: "Sync Status",
    SEARCH: {
      PLACEHOLDER: "Search by url",
    },
    HEADERS: [
      { id: "page_url", label: "Page URL", width: "w-[40%]" },
      { id: "status", label: "Live status", width: "w-1/5" },
      { id: "playground_status", label: "Playground status", width: "w-1/5" },
      { id: "extraction_status", label: "Extraction status", width: "w-1/5" },
      { id: "last_synced_at", label: "Last sync on", width: "w-1/3" },
    ],
    NO_DATA: {
      MESSAGE: "No websites found matching your search",
    },
    NO_CONTENT_SCRAPED: "No Content Scraped",
    WEBSITE_EXISTS_ERROR: "website already exists",
  },
  DOCUMENT_ACTIONS: {
    DOWNLOAD: "download",
    PREVIEW: "preview",
    DELETE: "delete",
    INDIVIDUAL_PUBLISH: "individual-publish",
    PUBLISH_DELETE: "publish_delete",
    DELINK: "delink",
    REUPLOAD: "reupload",
  },
  WEBSITE_ACTIONS: {
    GO_LIVE: "go_live",
    DELETE_FROM_LIVE: "delete_from_live",
    DELETE_FROM_PLAYGROUND: "delete_from_playground",
    RESYNC: "resync",
  },
  DOWNLOAD_FORMATS: ["pdf", "docx", "xlsx"],
  MESSAGES: {
    MODAL: {
      DOWNLOAD: {
        TITLE: "Download",
        CONFIRM: "Confirm Download",
      },
      UPLOAD: {
        TITLE: "Content Upload",
        UPLOAD_TEXT: "Upload",
      },
      REUPLOAD: {
        TITLE: "Re-Upload Document",
      },
      SYNC_WEBSITE: {
        TITLE: "Sync content from any website.",
        HEADING: "Website Link",
        DESCRIPTION:
          "Verbaflo scrapes your website to train its AI, enabling a deeper understanding of your business for more accurate and personalized communications.",
        URL_PLACEHOLDER: "https://verbaflo.ai",
        WEBSITE_EXISTS_ERROR: "Website url already exists.",
        INFO_TEXT:
          "For best results, enter a publicly accessible root domain (e.g., https://myhelpcenter.com). Verbaflo will auto-sync all linked pages, Avoid deep links for full coverage.",
        CANCEL: "Cancel",
        SAVE: "Save",
        SUCCESS: "Website links scraping initiated",
        ERROR: "Error syncing links",
      },
    },
    SUCCESS: {
      UPLOAD: (count: number) => `Successfully uploaded ${count} documents`,
      DOCUMENT_DELETED: "Document deletion in progress",
      WEBSITE_DELETED: "Website deletion in progress",
      DOCUMENT_DELETED_PLAYGROUND: "Document deleted from Playground",
      WEBSITE_DELETED_PLAYGROUND: "Website deleted from Playground",
      DOCUMENT_PUBLISHED: "Document publish in progress",
      WEBSITE_PUBLISHED: "Website publish in progress",
      DOCUMENT_DELINKED: "Document delinked successfully",
      DOCUMENT_DOWNLOADED: "Document downloaded successfully",
      DOCUMENT_PREVIEWED: "Document preview opened successfully",
      REUPLOAD_INITIATED: "Reupload successful. Ingestion in progress",
      WEBSITE_RESYNC_INITIATED: "Website resync initiated successfully",
      WEBSITE_RESYNC_PROGRESS: "Website resync is in progress",
      WEBSITE_SYNCED: "Website synced successfully",
    },
    ERROR: {
      GET_UNANSWERED_QUERY_BY_ID_FAILED: "Failed to fetch unanswered query",
      UPLOAD_FAILED: "Failed to upload documents",
      UPLOAD_PROCESSING_FAILED: "Files uploaded but processing failed",
      DELETE_DOCUMENT_FAILED: "Failed to delete document",
      DELETE_WEBSITE_FAILED: "Failed to delete website",
      PUBLISH_DOCUMENT_FAILED: "Failed to publish document",
      PUBLISH_WEBSITE_FAILED: "Failed to publish website",
      DOWNLOAD_FAILED: "Failed to download document",
      PREVIEW_FAILED: "Failed to open document preview",
      FILE_NOT_DOWNLOADABLE: "This file is either broken or has been removed from the playground/live environment, so it cannot be accessed.",
      REUPLOAD_FAILED: "Failed to reupload file",
      NO_FILE_PROVIDED: "No file provided",
      WEBSITE_RESYNC_FAILED: "Failed to resync website",
      WEBSITE_SYNC_FAILED: "Failed to sync website. Please try again.",
      WEBSITE_EXISTS: "Website already exists",
    },
    CONFIRMATION: {
      DELETE_DOCUMENT: {
        HEADING: "Are you sure you want to delete this document?",
        MESSAGE: [
          "This document will be permanently removed from your knowledge base",
          "Once deleted, it will no longer be available for AI responses",
        ],
        CONFIRM: "Yes, Delete!",
        CANCEL: "No, Keep it",
      },
      DELETE_WEBSITE: {
        HEADING: "Are you sure you want to delete this website?",
        MESSAGE: [
          "This document will be permanently removed from your knowledge base",
          "Once deleted, it will no longer be available for AI responses",
        ],
        CONFIRM: "Yes, Delete!",
        CANCEL: "No, Keep it",
      },
      DELETE_FROM_PLAYGROUND: {
        DOCUMENT: {
          MESSAGE: ["This document will be removed from playground"],
          CONFIRM: "Yes, Delete!",
          CANCEL: "No, Keep it",
        },
        WEBSITE: {
          HEADING: "Are you sure you want to remove this website?",
          MESSAGE: ["This document will be removed from playground"],
          CONFIRM: "Yes, Remove!",
          CANCEL: "No, Keep it",
        },
      },
      PUBLISH: {
        DOCUMENT: {
          HEADING: "Are you sure you want to publish this document?",
          MESSAGE: [
            "Once published, your updates will",
            "instantly reflect across all AI responses",
          ],
          CONFIRM: "Yes, Publish!",
          CANCEL: "Cancel",
        },
        WEBSITE: {
          HEADING: "Are you sure you want to publish this website?",
          MESSAGE: [
            "Once published, your updates will",
            "instantly reflect across all AI responses",
          ],
          CONFIRM: "Yes, Publish!",
          CANCEL: "No, Keep it",
        },
      },
      DELINK: {
        HEADING: "Are you sure you want to unlink this document?",
        MESSAGE: [
          "This will disconnect the document from your",
          "AI responses. It can be relinked anytime.",
        ],
        CONFIRM: "Unlink",
        CANCEL: "Cancel",
      },
    },
  },
  DIRECTORY_DETAILS: {
    TITLE: {
      PREFIX: "Sync content from",
    },
    SEARCH: {
      PLACEHOLDER: "Search by page name and url",
    },
    CONTENT: {
      HEADING: "Select page urls to sync",
      DESCRIPTION: "We'll sync all the content from the selected page urls.",
    },
    LOADING: {
      MESSAGE: "We're syncing all your website page urls.",
      SUBMESSAGE: "This won't take long",
      NO_URLS: "No URLS found",
    },
    FOOTER: {
      SELECTED_PREFIX: "Selected",
      SELECTED_SUFFIX: "page urls",
      CANCEL: "Cancel",
      SYNC: "Sync selected urls",
    },
    MESSAGES: {
      SYNC_SUCCESS: "Content scraping initiated",
      SYNC_ERROR: "Error syncing content",
    },
  },
} as const;
export type DocumentStatus =
  (typeof PLAYGROUND_CONFIG.DOCUMENT_STATUS)[keyof typeof PLAYGROUND_CONFIG.DOCUMENT_STATUS];
export type DocumentAction =
  (typeof PLAYGROUND_CONFIG.DOCUMENT_ACTIONS)[keyof typeof PLAYGROUND_CONFIG.DOCUMENT_ACTIONS];
export type WebsiteAction =
  (typeof PLAYGROUND_CONFIG.WEBSITE_ACTIONS)[keyof typeof PLAYGROUND_CONFIG.WEBSITE_ACTIONS];
export type PlaygroundTab = (typeof PLAYGROUND_CONFIG.TABS.VALID_TABS)[number];
export type PlaygroundTabItem = (typeof PLAYGROUND_CONFIG.TABS.LIST)[number];

export const getPlaygroundTabsList = (
  clientCode?: string
): { id: PlaygroundTab; label: string }[] =>
  PLAYGROUND_CONFIG.TABS.LIST.map((tab) =>
    tab.id === "knowledge-base"
      ? { id: tab.id, label: getRegionLabel("knowledgeBaseSection", clientCode) }
      : { id: tab.id, label: tab.label }
  );

export const formatUrl = (url: string, parentUrl: string | null = null) => {
  if (!parentUrl) return url;

  try {
    const urlObj = new URL(url);
    const parentUrlObj = new URL(parentUrl);

    if (urlObj.hostname === parentUrlObj.hostname) {
      const fullPath = urlObj.href.substring(urlObj.origin.length);
      return fullPath || "/";
    }
  } catch (e) {
    console.error("Invalid URL:", e);
  }

  return url;
};

/** Job status helpers – use status received from backend */
export const isCompletedStatus = (status: string): boolean => {
  return (
    status === ScrapeJobStatus.COMPLETED_LINK_SCRAPE ||
    status === ScrapeJobStatus.COMPLETED_CONTENT_SCRAPE ||
    status === ScrapeJobStatus.PARTIALLY_COMPLETED_CONTENT_SCRAPE
  );
};

export const isExtractingStatus = (status: string): boolean => {
  return (
    status === ScrapeJobStatus.INITIATED ||
    status === ScrapeJobStatus.SCRAPING_LINKS ||
    status === ScrapeJobStatus.SCRAPING_CONTENT
  );
};

export const isFailedStatus = (status: string): boolean => {
  return (
    status === ScrapeJobStatus.FAILED_LINK_SCRAPE ||
    status === ScrapeJobStatus.FAILED_CONTENT_SCRAPE
  );
};

/** Node status helpers – use status received from backend */
export const isCompletedNodeStatus = (status: string): boolean => {
  return status === ScrapeNodeStatus.COMPLETED
};

export const isExtractingNodeStatus = (status: string): boolean => {
  return (
    status === ScrapeNodeStatus.INITIATED ||
    status === ScrapeNodeStatus.SCRAPING
  );
};

export const isFailedNodeStatus = (status: string): boolean => {
  return status === ScrapeNodeStatus.FAILED;
};

export const HideNavTabsFor = ["unanswered-questions", "flagged-responses"];
